import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 as DeleteIcon, Copy as ContentCopyIcon } from 'lucide-react';
import type { Draft } from 'immer';
import { toast } from 'sonner';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import DeleteDialog from './DeleteDialog';
import { Webhook } from '../../../../../types/state';
import LearnMore from './LearnMore';

type WebhookWithType = Draft<Webhook> & { type: 'team' | 'user' };

interface WebhooksProps {
  webhooks: WebhookWithType[];
  onRemoveWebhook: (url: string, type: 'team' | 'user') => Promise<void>;
  note?: React.ReactNode;
  hideLearnMore?: boolean;
  emptyStateButton?: React.ReactNode;
}

const Webhooks: React.FC<WebhooksProps> = ({
  webhooks,
  onRemoveWebhook,
  note,
  hideLearnMore,
  emptyStateButton,
}) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({
    deleteDialogOpen: false,
    webhookToDelete: null as WebhookWithType | null,
  });
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);

  const updateFormState = (updates: Partial<typeof formState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  };

  const handleRemoveWebhook = (webhook: WebhookWithType) => {
    updateFormState({
      webhookToDelete: webhook,
      deleteDialogOpen: true,
    });
  };

  const handleConfirmDelete = async () => {
    const { webhookToDelete } = formState;
    if (!webhookToDelete) return;

    try {
      await onRemoveWebhook(webhookToDelete.url, webhookToDelete.type);
      toast(t('settings.integrations.webhooks.list.messages.webhookRemoved'));
      updateFormState({
        deleteDialogOpen: false,
        webhookToDelete: null,
      });
    } catch (err: any) {
      toast.error(
        t('settings.integrations.webhooks.list.messages.webhookRemoveFailed', {
          error: err.message,
        }),
      );
    }
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(
        t('settings.integrations.webhooks.list.messages.urlCopied', { label }),
      );
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="space-y-6">
      {!hideLearnMore && (
        <div className="pb-2 border-b border-gray-200 dark:border-gray-700">
          <Button
            variant="minimal"
            onClick={() => setLearnMoreOpen(true)}
            className="text-black hover:text-black"
          >
            {t('settings.integrations.webhooks.learnMore.button')}
          </Button>
        </div>
      )}

      {!hideLearnMore && (
        <LearnMore
          open={learnMoreOpen}
          onClose={() => setLearnMoreOpen(false)}
          note={note}
        />
      )}

      {webhooks.length > 0 ? (
        <div className="space-y-4">
          {webhooks.map((webhook) => (
            <div key={webhook.url} className="border rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-sm truncate">
                      {webhook.name}
                    </h4>
                    <Badge
                      variant="outline"
                      className={`text-xs ${webhook.type === 'team' ? 'text-blue-600 border-blue-200' : 'text-purple-600 border-purple-200'}`}
                    >
                      {webhook.type === 'team' ? 'Team' : 'Private'}
                    </Badge>
                  </div>

                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => handleCopy(webhook.url, 'Webhook URL')}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors cursor-pointer truncate text-left"
                    >
                      {webhook.url}
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="minimal"
                    size="sm"
                    onClick={() => handleCopy(webhook.url, 'Webhook URL')}
                    className="text-black hover:text-black h-6 w-6 p-0"
                  >
                    <ContentCopyIcon className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="minimal"
                    size="sm"
                    className="text-red-600 hover:text-red-700 h-6 w-6 p-0"
                    onClick={() => handleRemoveWebhook(webhook)}
                  >
                    <DeleteIcon className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p className="mb-4">
            {t('settings.integrations.webhooks.list.empty')}
          </p>
          {emptyStateButton}
        </div>
      )}

      <DeleteDialog
        open={formState.deleteDialogOpen}
        webhookName={formState.webhookToDelete?.name || ''}
        onClose={() => updateFormState({ deleteDialogOpen: false })}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default Webhooks;
